import React from 'react';
import { motion } from 'framer-motion';

const T = {
  bg: '#000', raised: '#111', border: '#1C1C1E',
  text: '#FFF', sec: '#8E8E93', ter: '#48484A',
  accent: '#007AFF', accentDim: 'rgba(0,122,255,0.12)',
  orange: '#FF9F0A', orangeDim: 'rgba(255,159,10,0.12)',
  green: '#30D158', greenDim: 'rgba(48,209,88,0.12)',
};

const ColabIcon = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
    <circle cx="11" cy="18" r="7" stroke="#FF9F0A" strokeWidth="2.4" />
    <circle cx="25" cy="18" r="7" stroke="#FF9F0A" strokeWidth="2.4" />
    <path d="M11 11l8 14" stroke="#FF9F0A" strokeWidth="2.4" strokeLinecap="round" opacity="0.5" />
  </svg>
);

const GitHubIcon = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
    <path
      d="M18 3C9.7 3 3 9.7 3 18c0 6.6 4.3 12.2 10.2 14.2.7.1 1-.3 1-.7v-2.6c-4.2.9-5-2-5-2-.7-1.7-1.7-2.2-1.7-2.2-1.4-.9.1-.9.1-.9 1.5.1 2.3 1.6 2.3 1.6 1.3 2.3 3.5 1.6 4.4 1.2.1-1 .5-1.6 1-2-3.3-.4-6.8-1.7-6.8-7.4 0-1.6.6-3 1.6-4-.2-.4-.7-2 .1-4.2 0 0 1.3-.4 4.1 1.5 1.2-.3 2.5-.5 3.7-.5s2.5.2 3.7.5c2.8-1.9 4.1-1.5 4.1-1.5.8 2.2.3 3.8.1 4.2 1 1 1.6 2.4 1.6 4 0 5.7-3.5 7-6.8 7.4.5.5 1 1.4 1 2.8v4.1c0 .4.3.8 1 .7C28.7 30.2 33 24.6 33 18c0-8.3-6.7-15-15-15z"
      fill="#FFFFFF"
    />
  </svg>
);

const ReportIcon = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
    <path
      d="M10 4h12l8 8v18a2 2 0 01-2 2H10a2 2 0 01-2-2V6a2 2 0 012-2z"
      stroke="#30D158" strokeWidth="2.2" strokeLinejoin="round" fill="rgba(48,209,88,0.08)"
    />
    <path d="M22 4v8h8" stroke="#30D158" strokeWidth="2.2" strokeLinejoin="round" />
    <path d="M13 20h10M13 24h10M13 28h6" stroke="#30D158" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const LinkTile = ({ label, url, icon, color, dimColor, delay }) => {
  const enabled = url && url !== '#';
  return (
    <motion.a
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.35 }}
      whileHover={enabled ? { y: -3 } : {}}
      whileTap={enabled ? { scale: 0.96 } : {}}
      href={enabled ? url : undefined}
      target={enabled ? '_blank' : undefined}
      rel={enabled ? 'noopener noreferrer' : undefined}
      onClick={(e) => { if (!enabled) e.preventDefault(); }}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
        padding: '20px 14px', borderRadius: 16,
        background: T.raised, border: `1px solid ${T.border}`,
        textDecoration: 'none', cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.45,
        transition: 'border-color 0.2s, background 0.2s',
        boxShadow: '0 4px 14px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)',
      }}
      onMouseEnter={(e) => { if (enabled) e.currentTarget.style.borderColor = color + '66'; }}
      onMouseLeave={(e) => { if (enabled) e.currentTarget.style.borderColor = T.border; }}
    >
      <div
        style={{
          width: 64, height: 64, borderRadius: 16,
          background: dimColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${color}33`,
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{label}</div>
      <div style={{ fontSize: 10, color: T.sec, fontFamily: "'SF Mono',monospace", letterSpacing: '0.05em' }}>
        {enabled ? 'OPEN ↗' : 'COMING SOON'}
      </div>
    </motion.a>
  );
};

export default function DeveloperProjectView({ project }) {
  if (!project) return null;

  const colab = project.notebookUrl || project.deepDive?.notebookUrl;
  const github = project.githubUrl;
  const report = project.reportUrl || project.deepDive?.reportUrl;

  return (
    <div
      style={{
        width: '100%', minHeight: '100%', background: T.bg, color: T.text,
        fontFamily: "-apple-system,BlinkMacSystemFont,'SF Pro Display',sans-serif",
      }}
    >
      <div className="max-w-4xl mx-auto" style={{ padding: '8px 24px 32px' }}>
        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '12px 0 4px' }}
        >
          {project.title}
        </motion.h1>
        {project.credential && (
          <div style={{ fontSize: 12, color: T.accent, fontWeight: 600, marginBottom: 10 }}>
            {project.credential}
          </div>
        )}
        <p style={{ fontSize: 13, color: T.sec, margin: '0 0 24px', lineHeight: 1.55, maxWidth: 640 }}>
          {project.deepDive?.description || project.deepDive?.subtitle || ''}
        </p>

        {(project.tags || []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 32 }}>
            {project.tags.map(t => (
              <span key={t} style={{
                fontSize: 10, fontWeight: 600, color: T.accent, background: T.accentDim,
                border: `1px solid ${T.accent}22`, padding: '3px 9px', borderRadius: 5,
                fontFamily: "'SF Mono',monospace",
              }}>{t}</span>
            ))}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 14,
            maxWidth: 560,
          }}
        >
          <LinkTile
            label="Google Colab"
            url={colab}
            icon={<ColabIcon />}
            color={T.orange}
            dimColor={T.orangeDim}
            delay={0}
          />
          <LinkTile
            label="GitHub Repo"
            url={github}
            icon={<GitHubIcon />}
            color={T.text}
            dimColor="rgba(255,255,255,0.06)"
            delay={0.06}
          />
          <LinkTile
            label="Report"
            url={report}
            icon={<ReportIcon />}
            color={T.green}
            dimColor={T.greenDim}
            delay={0.12}
          />
        </div>
      </div>
    </div>
  );
}
